import { useEffect, useState } from 'react';

import type { BrandKit } from '@/lib/api/types';

/**
 * Caso 05 v1 — Reusable celebration overlay for the Brand Studio "Test
 * celebration" simulation. CSS-only particles so we don't pull in a
 * dependency for a feature that lives behind a button.
 *
 * Themes:
 *  - confetti  → square paper bits drifting down the viewport
 *  - fireworks → radial bursts from a central origin
 *  - sparks    → twinkling dots near the centre
 *
 * The overlay self-dismisses after ~2.5 s (caller can override).
 */
export function CelebrationOverlay({
  theme,
  accent,
  durationMs = 2500,
  onDone,
}: {
  theme: BrandKit['celebrationTheme'];
  /** Accent colour for tinting particles. Defaults to brand purple. */
  accent?: string;
  durationMs?: number;
  onDone?: () => void;
}) {
  const [visible, setVisible] = useState(true);

  useEffect(() => {
    const id = window.setTimeout(() => {
      setVisible(false);
      onDone?.();
    }, durationMs);
    return () => window.clearTimeout(id);
  }, [durationMs, onDone]);

  if (!visible) return null;

  // Deterministic particle list keyed by theme — re-mount swaps the layout.
  const particles = makeParticles(theme ?? 'confetti', accent ?? 'var(--color-accent)');

  return (
    <div
      aria-hidden
      className="pointer-events-none absolute inset-0 overflow-hidden"
    >
      {particles}
      {/* Keyframes inline so the component is self-contained. */}
      <style>{KEYFRAMES}</style>
    </div>
  );
}

function makeParticles(
  theme: BrandKit['celebrationTheme'],
  accent: string,
) {
  if (theme === 'sparks') {
    return Array.from({ length: 18 }, (_, i) => (
      <span
        key={i}
        className="absolute h-1.5 w-1.5 rounded-full opacity-0"
        style={{
          left: `${10 + (i * 4.5) % 80}%`,
          top: `${15 + (i * 7) % 70}%`,
          background: accent,
          animation: `xk-spark 1.6s ease-out ${(i % 6) * 0.08}s infinite`,
          boxShadow: `0 0 8px ${accent}`,
        }}
      />
    ));
  }
  if (theme === 'fireworks') {
    return Array.from({ length: 24 }, (_, i) => {
      const angle = (i / 24) * 360;
      const wave = Math.floor(i / 8);
      return (
        <span
          key={i}
          className="absolute h-1.5 w-1.5 rounded-full"
          style={{
            left: '50%',
            top: '40%',
            background: i % 3 === 0 ? '#fff' : accent,
            transform: `rotate(${angle}deg)`,
            transformOrigin: '50% 50%',
            animation: `xk-firework 1.2s ease-out ${wave * 0.35}s both`,
          }}
        />
      );
    });
  }
  // Default: confetti.
  return Array.from({ length: 36 }, (_, i) => {
    const colors = [accent, '#FFD166', '#06D6A0', '#EF476F', '#118AB2'];
    return (
      <span
        key={i}
        className="absolute h-2 w-2"
        style={{
          left: `${(i * 2.8) % 100}%`,
          top: '-8px',
          background: colors[i % colors.length],
          transform: `rotate(${(i * 27) % 360}deg)`,
          animation: `xk-confetti ${1.6 + (i % 5) * 0.2}s ease-in ${(i % 8) * 0.06}s forwards`,
        }}
      />
    );
  });
}

const KEYFRAMES = `
@keyframes xk-confetti {
  0%   { transform: translateY(-10vh) rotate(0deg);   opacity: 1; }
  100% { transform: translateY(110vh) rotate(720deg); opacity: 0.7; }
}
@keyframes xk-spark {
  0%, 100% { transform: scale(0.6); opacity: 0; }
  50%      { transform: scale(1.4); opacity: 1; }
}
@keyframes xk-firework {
  0%   { transform: translate(-50%, -50%) rotate(var(--rot, 0deg)) translateX(0) scale(1); opacity: 1; }
  100% { transform: translate(-50%, -50%) rotate(var(--rot, 0deg)) translateX(140px) scale(0); opacity: 0; }
}
`;
